import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye, Trash2, Loader2, Lock, CheckCircle, Clock, Copy, CreditCard, Palette, ImageOff, UserCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import AppHeader from '@/components/AppHeader';

// Use current origin for API calls to work in both preview and production
const API = `${window.location.origin}/api`;
const ADMIN_EMAIL = 'pgundepudi@gmail.com';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { workspace, isOwner, fetchWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [reviewRequests, setReviewRequests] = useState([]);
  const [reviewers, setReviewers] = useState([]);

  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    fetchUserAndProjects();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchReviewRequests();
      fetchReviewers();
    }
  }, [isAdmin]);

  const fetchUserAndProjects = async () => {
    try {
      const projectsRes = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewRequests = async () => {
    try {
      const response = await axios.get(`${API}/projects/admin/frozen`, { withCredentials: true });
      // Filter to only pending_review status
      const pending = (response.data || []).filter(p => p.status === 'pending_review');
      setReviewRequests(pending);
    } catch (error) {
      console.error('Error fetching review requests:', error);
    }
  };

  const fetchReviewers = async () => {
    try {
      const response = await axios.get(`${API}/projects/admin/design-reviewers`, { withCredentials: true });
      setReviewers(response.data.reviewers || []);
    } catch (error) {
      console.error('Error fetching reviewers:', error);
    }
  };

  const assignReviewer = async (projectId, reviewerId) => {
    try {
      // Find reviewer email from ID
      const reviewer = reviewers.find(r => r.user_id === reviewerId || r.member_email === reviewerId);
      const assigneeEmail = reviewer?.member_email || reviewer?.email || reviewerId;
      
      await axios.put(`${API}/projects/admin/project/${projectId}/assign`, { assignee_email: assigneeEmail }, { withCredentials: true });
      toast.success('Reviewer assigned successfully');
      fetchReviewRequests();
    } catch (error) {
      toast.error('Failed to assign reviewer');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    setDeleting(projectId);

    try {
      await axios.delete(`${API}/projects/${projectId}`, { withCredentials: true });
      toast.success('Project deleted');
      setProjects(projects.filter(p => p.project_id !== projectId));
    } catch (error) {
      toast.error('Failed to delete project');
      console.error(error);
    } finally {
      setDeleting(null);
    }
  };

  const handleDuplicateProject = async (project) => {
    try {
      // Create a new project based on the existing one
      const response = await axios.post(
        `${API}/projects`,
        {
          business_info: {
            ...project.business_info,
            business_name: `${project.business_info.business_name} (Copy)`
          }
        },
        { withCredentials: true }
      );
      
      toast.success('Project duplicated! You can now edit the new version.');
      navigate(`/themes/${response.data.project_id}`);
    } catch (error) {
      toast.error('Failed to duplicate project');
      console.error(error);
    }
  };

  const getStatusBadge = (status, adminApproved = false) => {
    if (adminApproved) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-600">
          <CheckCircle className="w-3 h-3" />
          Finalized
        </span>
      );
    }
    
    const statusConfig = {
      briefing: { label: 'Briefing', color: 'bg-blue-100 text-blue-700', icon: null },
      theme_selection: { label: 'Theme Selection', color: 'bg-purple-100 text-purple-700', icon: null },
      design_preview: { label: 'Design Preview', color: 'bg-yellow-100 text-yellow-700', icon: null },
      design_generated: { label: 'Design Ready', color: 'bg-green-100 text-green-700', icon: null },
      frozen: { label: 'Frozen', color: 'bg-green-100 text-green-700', icon: Lock },
      pending_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock }
    };

    const config = statusConfig[status] || statusConfig.briefing;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {Icon && <Icon className="w-3 h-3" />}
        {config.label}
      </span>
    );
  };

  // Separate projects by status
  const activeProjects = projects.filter(p => !['frozen', 'pending_review'].includes(p.status) && !p.admin_approved);
  const pendingProjects = projects.filter(p => ['frozen', 'pending_review'].includes(p.status) && !p.admin_approved);
  const finalizedProjects = projects.filter(p => p.admin_approved);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader title={workspace?.name || 'Dashboard'} />

      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Create New Project Button */}
          <div className="mb-8 flex items-center justify-between">
            <Button
              data-testid="create-new-project-btn"
              size="lg"
              onClick={() => navigate('/briefing')}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <Plus className="w-5 h-5 mr-2" /> Create New Project
            </Button>
          </div>

          {/* Admin: Design Review Requests */}
          {isAdmin && reviewRequests.length > 0 && (
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-zinc-900">Design Review Requests</h2>
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                  {reviewRequests.length} pending
                </span>
              </div>
              <p className="text-sm text-zinc-600 mb-4">Customer designs awaiting review. Assign to a Design Reviewer to process.</p>
              <div className="space-y-3">
                {reviewRequests.map((project) => (
                  <Card key={project.project_id} className="bg-white border-amber-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-zinc-900 truncate">{project.business_name || 'Untitled Project'}</h3>
                          <p className="text-sm text-zinc-500">{project.customer_email || project.user_email}</p>
                          <p className="text-xs text-zinc-400 mt-1">
                            Submitted: {new Date(project.updated_at || project.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          {project.assigned_reviewer ? (
                            <span className="text-sm text-green-600 flex items-center gap-1">
                              <UserCheck className="w-4 h-4" />
                              Assigned
                            </span>
                          ) : (
                            <Select onValueChange={(reviewerId) => assignReviewer(project.project_id, reviewerId)}>
                              <SelectTrigger className="w-[180px]" data-testid={`assign-reviewer-${project.project_id}`}>
                                <SelectValue placeholder="Assign Reviewer" />
                              </SelectTrigger>
                              <SelectContent>
                                {reviewers.length > 0 ? reviewers.map(reviewer => (
                                  <SelectItem key={reviewer.user_id} value={reviewer.user_id}>
                                    {reviewer.name || reviewer.email}
                                  </SelectItem>
                                )) : (
                                  <SelectItem value="none" disabled>No reviewers available</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/design-preview/${project.project_id}`)}
                            className="border-amber-300 text-amber-700"
                          >
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Active Projects Section */}
          {activeProjects.length > 0 && (
            <div className="mb-10">
              <h2 className="text-lg font-semibold text-zinc-900 mb-4">Active Projects</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activeProjects.map((project, index) => (
                  <ProjectCard
                    key={project.project_id}
                    project={project}
                    index={index}
                    onDelete={handleDeleteProject}
                    onDuplicate={handleDuplicateProject}
                    deleting={deleting}
                    getStatusBadge={getStatusBadge}
                    navigate={navigate}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Pending Review Projects */}
          {pendingProjects.length > 0 && (
            <div className="mb-10">
              <h2 className="text-lg font-semibold text-zinc-900 mb-4">Pending Review</h2>
              <p className="text-sm text-zinc-600 mb-4">These designs have been submitted and are awaiting admin review.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pendingProjects.map((project, index) => (
                  <ProjectCard
                    key={project.project_id}
                    project={project}
                    index={index}
                    onDelete={() => {}}
                    onDuplicate={handleDuplicateProject}
                    deleting={null}
                    getStatusBadge={getStatusBadge}
                    navigate={navigate}
                    readOnly
                  />
                ))}
              </div>
            </div>
          )}

          {/* Finalized Projects */}
          {finalizedProjects.length > 0 && (
            <div className="mb-10">
              <h2 className="text-lg font-semibold text-zinc-900 mb-4">Finalized Designs</h2>
              <p className="text-sm text-zinc-600 mb-4">These designs have been approved and finalized.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {finalizedProjects.map((project, index) => (
                  <ProjectCard
                    key={project.project_id}
                    project={project}
                    index={index}
                    onDelete={() => {}}
                    onDuplicate={handleDuplicateProject}
                    deleting={null}
                    getStatusBadge={getStatusBadge}
                    navigate={navigate}
                    readOnly
                    finalized
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {projects.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-zinc-100 flex items-center justify-center">
                  <Plus className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{fontFamily: 'Outfit'}}>No projects yet</h3>
                <p className="text-zinc-600 mb-6">Start by creating your first AI-powered website design</p>
                <Button
                  data-testid="create-first-project-btn"
                  onClick={() => navigate('/briefing')}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  Create Your First Project
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

// Project Card Component
const ProjectCard = ({ project, index, onDelete, onDuplicate, deleting, getStatusBadge, navigate, readOnly = false, finalized = false }) => {
  // Determine what to show in the thumbnail area
  const hasDesign = ['design_preview', 'frozen', 'pending_review'].includes(project.status) || project.admin_approved;
  const hasTheme = !!project.selected_theme_id;
  const hasScreenshot = !!project.homepage_screenshot;
  
  const renderThumbnail = () => {
    // Priority 1: Show homepage screenshot if available
    if (hasScreenshot) {
      return (
        <div className="w-full h-36 bg-zinc-100 overflow-hidden">
          <img 
            src={project.homepage_screenshot} 
            alt={`${project.business_info?.business_name || 'Project'} homepage preview`}
            className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.classList.add('flex', 'items-center', 'justify-center', 'flex-col', 'gap-2');
              e.target.parentElement.innerHTML = '<svg class="w-8 h-8 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg><span class="text-zinc-400 text-xs">Preview unavailable</span>';
            }}
          />
        </div>
      );
    }
    
    // Priority 2: Show theme thumbnail as fallback if design is generated
    if (hasDesign && project.theme_thumbnail) {
      return (
        <div className="w-full h-36 bg-zinc-100 overflow-hidden">
          <img 
            src={project.theme_thumbnail} 
            alt={`${project.business_info?.business_name || 'Project'} theme preview`}
            className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.classList.add('flex', 'items-center', 'justify-center', 'flex-col', 'gap-2');
              e.target.parentElement.innerHTML = '<svg class="w-8 h-8 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg><span class="text-zinc-400 text-xs">Preview unavailable</span>';
            }}
          />
        </div>
      );
    }
    
    // Design generated but no screenshot captured yet - prompt to view
    if (hasDesign && !hasScreenshot) {
      return (
        <div className="w-full h-36 bg-gradient-to-br from-amber-50 to-yellow-50 flex flex-col items-center justify-center gap-2">
          <Eye className="w-8 h-8 text-amber-300" />
          <span className="text-amber-600/60 text-xs font-medium">View to generate preview</span>
        </div>
      );
    }
    
    // Theme selected but design not generated
    if (hasTheme && !hasDesign) {
      return (
        <div className="w-full h-36 bg-gradient-to-br from-zinc-50 to-zinc-100 flex flex-col items-center justify-center gap-2">
          <Palette className="w-8 h-8 text-zinc-300" />
          <span className="text-zinc-400 text-xs font-medium">Design not generated</span>
        </div>
      );
    }
    
    // Theme not selected
    return (
      <div className="w-full h-36 bg-gradient-to-br from-zinc-50 to-zinc-100 flex flex-col items-center justify-center gap-2">
        <Palette className="w-8 h-8 text-zinc-300" />
        <span className="text-zinc-400 text-xs font-medium">Theme not selected</span>
      </div>
    );
  };
  
  return (
    <div>
      <Card data-testid={`project-card-${project.project_id}`} className="card-hover h-full overflow-hidden">
        {/* Thumbnail Preview */}
        {renderThumbnail()}
        
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {project.display_id && (
                  <span className="text-xs font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                    {project.display_id}
                  </span>
                )}
                {getStatusBadge(project.status, finalized)}
              </div>
              <CardTitle className="text-base" style={{fontFamily: 'Outfit'}}>
                {project.business_info?.business_name || 'Untitled Project'}
              </CardTitle>
              <p className="text-xs text-zinc-600 mt-0.5">{project.business_info?.business_type}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-xs text-zinc-600 mb-3 line-clamp-2">
            {project.business_info?.business_description}
          </p>
          
          <div className="text-xs text-zinc-500 mb-3">
            Created {new Date(project.created_at).toLocaleDateString()}
          </div>
          
          <div className="flex gap-2">
            {project.status === 'theme_selection' && !readOnly && (
              <Button
                data-testid={`select-theme-btn-${project.project_id}`}
                size="sm"
                onClick={() => navigate(`/themes/${project.project_id}`)}
                className="flex-1 bg-amber-500 hover:bg-amber-600"
              >
                Select Theme
              </Button>
            )}
            
            {(project.status === 'design_preview' || project.status === 'frozen' || project.status === 'pending_review' || finalized) && (
              <Button
                data-testid={`view-design-btn-${project.project_id}`}
                size="sm"
                onClick={() => navigate(`/preview/${project.project_id}`)}
                className={`flex-1 ${readOnly || project.status === 'pending_review' ? '' : 'bg-amber-500 hover:bg-amber-600'}`}
                variant={readOnly || project.status === 'pending_review' ? 'outline' : 'default'}
              >
                <Eye className="w-4 h-4 mr-2" /> View
              </Button>
            )}
            
            {/* Start New from This Design */}
            {(readOnly || finalized || project.status === 'pending_review') && (
              <Button
                data-testid={`duplicate-btn-${project.project_id}`}
                size="sm"
                variant="outline"
                onClick={() => onDuplicate(project)}
                title="Create new project based on this design"
              >
                <Copy className="w-4 h-4" />
              </Button>
            )}
            
            {!readOnly && project.status !== 'frozen' && (
              <Button
                data-testid={`delete-btn-${project.project_id}`}
                size="sm"
                variant="outline"
                onClick={() => onDelete(project.project_id)}
                disabled={deleting === project.project_id}
              >
                {deleting === project.project_id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;